import React, { useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { FaArrowCircleUp, FaTrash, FaEdit, FaEye, FaTimes, FaCheck, FaFilter, FaGripVertical } from "react-icons/fa";
import { FiDownload, FiRefreshCw, FiSave } from "react-icons/fi";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.bookwie.com';

const FileUploadServices = () => {
  const [banner1File, setBanner1File] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Reorder states
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  // Fetch the categories list
  const fetchCategories = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    if (!token) {
      console.error("No token found");
      setUploadError("Authentication token is missing.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/service-categories`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      console.log("Fetched categories:", data);

      if (data.success) {
        setCategories(data.data);
        setFilteredCategories(data.data); // Initialize filtered categories
      } else {
        console.error("Error fetching categories:", data.message);
        setUploadError("Failed to fetch categories.");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setUploadError("Network error while fetching categories.");
    } finally {
      setLoading(false);
    }
  };

  // Apply filters
  useEffect(() => {
    let filtered = [...categories];

    // Apply search filter
    if (searchFilter.trim()) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchFilter.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(category => {
        if (statusFilter === 'active') {
          return category.status === 'active';
        } else if (statusFilter === 'inactive') {
          return category.status === 'inactive' || !category.status;
        }
        return true;
      });
    }

    setFilteredCategories(filtered);
  }, [categories, searchFilter, statusFilter]);

  // Reset filters
  const resetFilters = () => {
    setSearchFilter('');
    setStatusFilter('');
  };

  // Handle file drop
  const onBanner1Drop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadError("File size must be less than 5MB");
        return;
      }
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError("Please select a valid image file");
        return;
      }
      
      setBanner1File(file);
      setUploadError(null);
    }
  };

  // Use dropzone hook
  const {
    getRootProps: getBanner1RootProps,
    getInputProps: getBanner1InputProps,
    isDragActive: isBanner1DragActive,
  } = useDropzone({
    onDrop: onBanner1Drop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    },
    multiple: false,
  });

  // Handle category name change
  const handleCategoryNameChange = (e) => {
    setCategoryName(e.target.value);
    if (uploadError && e.target.value.trim()) {
      setUploadError(null);
    }
  };

  // Reset form
  const resetForm = () => {
    setBanner1File(null);
    setCategoryName("");
    setUploadError(null);
    setUploadSuccess(null);
    setEditingCategory(null);
  };

  // Handle file upload and submit form
  const handleUpload = async () => {
    // Validation
    if (!categoryName.trim()) {
      setUploadError("Please provide a category name.");
      return;
    }

    if (!banner1File && !editingCategory) {
      setUploadError("Please select an image file.");
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);

    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authorization token is missing.");
      setUploading(false);
      return;
    }

    const formData = new FormData();
    formData.append("name", categoryName.trim());
    if (banner1File) {
      formData.append("image", banner1File);
    }

    try {
      const url = editingCategory
        ? `${API_BASE_URL}/api/admin/service-category/${editingCategory._id}`
        : `${API_BASE_URL}/api/admin/service-category`;
      
      const method = editingCategory ? "PATCH" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      console.log("Upload response:", data);
      console.log("Response status:", response.status);
      console.log("Response OK:", response.ok);

      if (data.success) {
        setUploadSuccess(
          editingCategory
            ? "Service Category updated successfully!"
            : "Service Category uploaded successfully!"
        );
        resetForm();
        fetchCategories();

        // Clear success message after 3 seconds
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        const errorMessage = data.message || data.error || "Error processing service category.";
        console.error("Backend error:", errorMessage);
        console.error("Full error response:", data);
        setUploadError(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error processing service category:", error);
      setUploadError("Network error occurred. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle edit category
  const handleEdit = (category) => {
    setEditingCategory(category);
    setCategoryName(category.name);
    setBanner1File(null);
    setUploadError(null);
    setUploadSuccess(null);
  };

  // Handle delete category
  const handleDelete = async (categoryId) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authorization token is missing.");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/admin/service-category/${categoryId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setUploadSuccess("Service Category deleted successfully!");
        fetchCategories();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error deleting service category.");
      }
    } catch (error) {
      console.error("Error deleting service category:", error);
      setUploadError("Network error occurred while deleting.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  // Handle toggle status
  const handleToggleStatus = async (category) => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUploadError("Authorization token is missing.");
      return;
    }

    try {
      const newStatus = category.status === 'active' ? 'inactive' : 'active';
      const response = await fetch(
        `${API_BASE_URL}/api/admin/service-category/${category._id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setUploadSuccess(`Service Category ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        fetchCategories();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || "Error updating category status.");
      }
    } catch (error) {
      console.error("Error updating category status:", error);
      setUploadError("Network error occurred while updating status.");
    }
  };

  // Enter / exit reorder mode
  const handleToggleReorderMode = () => {
    if (!isReorderMode) {
      setReorderList([...filteredCategories]);
    }
    setIsReorderMode(!isReorderMode);
  };

  // Drag-and-drop handlers
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
    const updated = [...reorderList];
    const dragged = updated.splice(dragItem.current, 1)[0];
    updated.splice(index, 0, dragged);
    dragItem.current = index;
    setReorderList(updated);
  };

  const handleDragEnd = () => {
    dragItem.current = null;
    dragOverItem.current = null;
  };

  // Save the new order to the backend
  const handleSaveOrder = async () => {
    setSavingOrder(true);
    const token = localStorage.getItem("token");
    try {
      const payload = reorderList.map((cat, index) => ({ id: cat._id, sortOrder: index }));
      const response = await fetch(`${API_BASE_URL}/api/admin/service-categories/reorder`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ categories: payload }),
      });
      const data = await response.json();
      if (data.success) {
        setUploadSuccess('Category order saved successfully!');
        setIsReorderMode(false);
        fetchCategories();
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        setUploadError(data.message || 'Failed to save order.');
      }
    } catch (error) {
      setUploadError('Network error while saving order.');
    } finally {
      setSavingOrder(false);
    }
  };

  // View category in modal
  const handleView = (category) => {
    setEditingCategory(category);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
  };

  // Export categories data
  const handleExport = () => {
    const csvContent = [
      ['Name', 'Status', 'Created At'],
      ...filteredCategories.map(category => [
        category.name,
        category.status || 'inactive',
        new Date(category.createdAt).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `service_categories_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Fetch categories when component mounts
  useEffect(() => {
    fetchCategories();
  }, []);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl shadow-card p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {editingCategory ? 'Edit Service Category' : 'Add New Service Category'}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={handleExport}
              disabled={filteredCategories.length === 0}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                filteredCategories.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              <FiDownload className="mr-2" />
              Export CSV ({filteredCategories.length})
            </button>
            <button
              onClick={fetchCategories}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiRefreshCw className="mr-2" />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="space-y-4">
            {/* File Upload */}
            <div className="border border-gray-300 border-dashed rounded-xl hover:border-blue-500 transition-colors">
              <div
                {...getBanner1RootProps()}
                className={`dropzone rounded-xl p-7 cursor-pointer transition-all
                  ${
                    isBanner1DragActive
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                  }
                `}
              >
                <input {...getBanner1InputProps()} />

                <div className="flex flex-col items-center">
                  <div className="mb-4 flex justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-200 text-gray-700">
                      <FaArrowCircleUp className="text-2xl" />
                    </div>
                  </div>

                  <h4 className="mb-3 font-semibold text-gray-800 text-lg">
                    {isBanner1DragActive
                      ? "Drop Files Here"
                      : "Drag & Drop Service Category Image"}
                  </h4>

                  <span className="text-center mb-3 block w-full max-w-xs text-sm text-gray-600">
                    Drag and drop your PNG, JPG, WebP, SVG images here or browse
                  </span>

                  <span className="font-medium text-blue-600 hover:text-blue-800 text-sm">
                    Browse File
                  </span>
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Max file size: 5MB
                  </div>
                </div>
              </div>
            </div>

            {/* Category Name Input */}
            <div>
              <label htmlFor="categoryName" className="block text-sm font-medium text-gray-700 mb-2">
                Category Name *
              </label>
              <input
                id="categoryName"
                type="text"
                value={categoryName}
                onChange={handleCategoryNameChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter category name..."
                maxLength={50}
              />
              <div className="mt-1 text-xs text-gray-500">
                {categoryName.length}/50 characters
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleUpload}
                disabled={uploading}
                className={`flex-1 py-3 px-4 rounded-lg font-medium transition-colors ${
                  uploading
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {uploading ? "Processing..." : editingCategory ? "Update Category" : "Upload Category"}
              </button>
              
              {editingCategory && (
                <button
                  onClick={resetForm}
                  className="px-4 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Status Messages */}
            {uploadSuccess && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                {uploadSuccess}
              </div>
            )}
            {uploadError && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                {uploadError}
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Preview</h3>
            {banner1File ? (
              <div className="border border-gray-300 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-3">Selected Image:</h5>
                <img
                  src={URL.createObjectURL(banner1File)}
                  alt="Preview"
                  className="w-full max-w-xs rounded-lg shadow-sm"
                />
                <div className="mt-2 text-sm text-gray-600">
                  Size: {(banner1File.size / 1024 / 1024).toFixed(2)} MB
                </div>
              </div>
            ) : editingCategory && editingCategory.image ? (
              <div className="border border-gray-300 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-3">Current Image:</h5>
                <img
                  src={editingCategory.image.startsWith("http") ? editingCategory.image : `${API_BASE_URL}${editingCategory.image}`}
                  alt={editingCategory.name}
                  className="w-full max-w-xs rounded-lg shadow-sm"
                />
              </div>
            ) : (
              <div className="border border-gray-300 border-dashed rounded-lg p-8 text-center text-gray-500">
                No image selected
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-3xl shadow-card p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            Service Categories ({filteredCategories.length})
          </h3>
          <div className="flex space-x-2">
            {isReorderMode ? (
              <>
                <button
                  onClick={handleSaveOrder}
                  disabled={savingOrder}
                  className={`flex items-center px-4 py-2 rounded-lg text-white transition-colors ${savingOrder ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  <FiSave className="mr-2" />
                  {savingOrder ? 'Saving...' : 'Save Order'}
                </button>
                <button
                  onClick={() => setIsReorderMode(false)}
                  className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <FaTimes className="mr-2" /> Cancel
                </button>
              </>
            ) : (
              <button
                onClick={handleToggleReorderMode}
                disabled={filteredCategories.length < 2}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${filteredCategories.length < 2 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
              >
                <FaGripVertical className="mr-2" /> Reorder
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <FaFilter className="mr-1" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={resetFilters}
                className="flex items-center px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                <FiRefreshCw className="mr-1" /> Reset Filters
              </button>
            </div>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search by Name</label>
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter category name..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2 text-gray-600">Loading categories...</span>
          </div>
        ) : filteredCategories.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg">No service categories found.</p>
            <p className="text-sm">
              {categories.length === 0 
                ? "Add your first category using the form above." 
                : "Try adjusting your filters or add new categories."}
            </p>
          </div>
        ) : isReorderMode ? (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-3">Drag rows to reorder, then click <strong>Save Order</strong>.</p>
            {reorderList.map((category, index) => (
              <div
                key={category._id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className="flex items-center bg-gray-50 border border-gray-200 rounded-lg p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow select-none"
              >
                <FaGripVertical className="text-gray-400 mr-3 flex-shrink-0" />
                <span className="w-6 h-6 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold mr-3 flex-shrink-0">
                  {index + 1}
                </span>
                {category.image && (
                  <img
                    src={category.image.startsWith("http") ? category.image : `${API_BASE_URL}${category.image}`}
                    alt={category.name}
                    className="w-10 h-10 object-cover rounded-md mr-3 flex-shrink-0"
                  />
                )}
                <span className="font-medium text-gray-800 flex-1">{category.name}</span>
                <span className={`px-2 py-1 text-xs rounded-full ml-2 ${category.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {category.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredCategories.map((category) => (
              <div
                key={category._id}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex justify-between items-start mb-3">
                  <h5 className="font-semibold text-gray-800 truncate flex-1 mr-2">
                    {category.name}
                  </h5>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => handleToggleStatus(category)}
                      className={`p-1 rounded ${
                        category.status === 'active' ? 'text-green-600 hover:bg-green-100' : 'text-gray-400 hover:bg-gray-200'
                      }`}
                      title={category.status === 'active' ? 'Active' : 'Inactive'}
                    >
                      <FaCheck className="text-sm" />
                    </button>
                  </div>
                </div>

                {category.image && (
                  <img
                    src={category.image.startsWith("http") ? category.image : `${API_BASE_URL}${category.image}`}
                    alt={category.name}
                    className="w-full h-32 object-cover rounded-lg mb-3"
                  />
                )}

                <div className="flex justify-between items-center">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    category.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {category.status === 'active' ? 'Active' : 'Inactive'}
                  </span>

                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleView(category)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                      title="View"
                    >
                      <FaEye className="text-sm" />
                    </button>
                    <button
                      onClick={() => handleEdit(category)}
                      className="p-2 text-yellow-600 hover:bg-yellow-100 rounded transition-colors"
                      title="Edit"
                    >
                      <FaEdit className="text-sm" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(category)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                      title="Delete"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal - Fixed positioning and close button */}
      {showModal && editingCategory && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
              <h3 className="text-xl font-bold">Category Details</h3>
              <button
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Close"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-600">Name:</label>
                <p className="text-lg font-semibold">{editingCategory.name}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Status:</label>
                <span className={`ml-2 px-3 py-1 text-sm rounded-full ${
                  editingCategory.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {editingCategory.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
              
              {editingCategory.image && (
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-2">Image:</label>
                  <img
                    src={editingCategory.image.startsWith("http") ? editingCategory.image : `${API_BASE_URL}${editingCategory.image}`}
                    alt={editingCategory.name}
                    className="w-full max-w-md rounded-lg shadow-sm mx-auto"
                  />
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Created:</label>
                  <p className="text-sm text-gray-500">
                    {formatDate(editingCategory.createdAt)}
                  </p>
                </div>
                
                {editingCategory.updatedAt && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Last Updated:</label>
                    <p className="text-sm text-gray-500">
                      {formatDate(editingCategory.updatedAt)}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-center space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    closeModal();
                    handleEdit(editingCategory);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Category
                </button>
                <button
                  onClick={() => handleToggleStatus(editingCategory)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    editingCategory.status === 'active'
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {editingCategory.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold">Confirm Deletion</h3>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"? This action cannot be undone and may affect vendors using this category.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm._id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploadServices;